/**
 * ReflectionCard - Aura's reflection on the choice + save/share + learning link
 */
import React from 'react';
import { motion } from 'framer-motion';
import { BookmarkIcon, ShareIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const ReflectionCard = ({ turn, session, onContinue, onSaveInsight, onShareCommunity }) => {
  const handleSaveInsight = async () => {
    try {
      await axios.post(
        `/api/game/city-of-signals/session/${session.session_id}/save-insight`,
        {
          session_id: session.session_id,
          turn_number: turn.turn_number,
          insight_text: turn.lesson_learned || turn.aura_reflection
        }
      );
      onSaveInsight?.();
      alert('Insight saved to Notes!');
    } catch (err) {
      console.error('Error saving insight:', err);
      alert('Failed to save insight');
    }
  };

  const handleShareCommunity = async () => {
    try {
      await axios.post(
        `/api/game/city-of-signals/session/${session.session_id}/share-community`,
        {
          session_id: session.session_id,
          turn_number: turn.turn_number
        }
      );
      onShareCommunity?.();
      alert('Shared to Community!');
    } catch (err) {
      console.error('Error sharing:', err);
      alert('Failed to share');
    }
  };

  return (
    <motion.div
      className="border border-hairline bg-bone-raised rounded-lg p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl" aria-hidden="true">
              ✨
            </span>
            <h3 className="font-display text-h3 text-primary">Aura&apos;s Reflection</h3>
          </div>
          <p className="text-body text-secondary leading-relaxed">{turn.aura_reflection}</p>
        </div>

        {turn.lesson_learned && (
          <div className="border border-hairline rounded-md p-4 bg-pine-tint">
            <div className="text-label text-pine font-medium mb-2">Lesson learned</div>
            <p className="text-body text-primary">{turn.lesson_learned}</p>
          </div>
        )}

        {turn.outcome?.meter_changes && (
          <div className="border border-hairline rounded-md p-4">
            <div className="text-label text-secondary font-medium mb-2">Impact</div>
            <div className="space-y-1 text-label">
              {Object.entries(turn.outcome.meter_changes).map(([meter, change]) => {
                if (Math.abs(change) < 1) return null;
                const sign = change > 0 ? '+' : '';
                const tone = change > 0 ? 'text-pine' : 'text-danger';
                return (
                  <div key={meter} className="flex justify-between">
                    <span className="capitalize text-secondary">{meter.replace('_', ' ')}</span>
                    <span className={`figure ${tone}`}>
                      {sign}
                      {change.toFixed(1)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {turn.citations && turn.citations.length > 0 && (
          <div className="border border-hairline rounded-md p-4">
            <div className="text-label text-secondary font-medium mb-2">Sources</div>
            <div className="space-y-2">
              {turn.citations.map((cite, idx) => (
                <a
                  key={idx}
                  href={cite.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-label text-pine hover:underline"
                >
                  {cite.title} ({cite.source})
                </a>
              ))}
            </div>
          </div>
        )}

        {turn.learning_recommendation && (
          <div className="border border-hairline rounded-md p-4 bg-pine-tint">
            <div className="text-label text-pine font-medium mb-2 flex items-center gap-2">
              <AcademicCapIcon className="w-5 h-5" />
              Learn more
            </div>
            <a
              href={turn.learning_recommendation.deep_link}
              className="text-body text-pine hover:underline"
            >
              {turn.learning_recommendation.title}
            </a>
          </div>
        )}

        <div className="flex flex-wrap gap-3 pt-4 border-t border-hairline">
          <button
            type="button"
            onClick={handleSaveInsight}
            className="btn-secondary flex-1 min-w-[140px] flex items-center justify-center gap-2 py-3 text-label"
          >
            <BookmarkIcon className="w-5 h-5" />
            Save Insight
          </button>
          <button
            type="button"
            onClick={handleShareCommunity}
            className="btn-secondary flex-1 min-w-[140px] flex items-center justify-center gap-2 py-3 text-label"
          >
            <ShareIcon className="w-5 h-5" />
            Share
          </button>
          <button
            type="button"
            onClick={onContinue}
            className="btn-primary flex-1 min-w-[140px] py-3 text-label"
          >
            Continue
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ReflectionCard;
